import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { HERO, HERO_STATS, LINKS } from "@/data/content";
import { useT, useLanguage } from "@/i18n/LanguageContext";

export const Hero = () => {
  const t = useT();
  const { lang } = useLanguage();
  const headlineLines = HERO.headline[lang];

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center wf-grain pt-24 pb-16"
    >
      <div className="absolute inset-0">
        <img
          src="/img/gallery/exterior-1.jpg"
          alt="Monte Casa Zlatibor"
          className="w-full h-full object-cover animate-kenburns opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAFAF7]/85 via-[#FAFAF7]/90 to-[#FAFAF7]" />
        <div className="absolute inset-0 wf-mesh" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full grid lg:grid-cols-[1.3fr_1fr] gap-12 items-center">
        {/* LEFT — Content */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-center gap-3"
          >
            <span
              data-testid="hero-prelaunch-badge"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E8E7E2] bg-white/80 text-xs uppercase tracking-[0.2em] text-[#1A1A1F] font-bold"
            >
              <span className="wf-pulse-dot w-2 h-2 rounded-full bg-[#0A0A0F]" />
              {t(HERO.badge)}
            </span>
            <span className="inline-block uppercase text-xs tracking-[0.25em] text-[#1A1A1F]/70 font-bold">
              {t(HERO.badgeSecondary)}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-headline"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tighter leading-[1.05] mt-7"
          >
            {headlineLines.map((line, i) => (
              <span key={i} className="block">
                {i === HERO.italicIndex ? (
                  <em className="not-italic font-light italic text-[#0A0A0F]">{line}</em>
                ) : (
                  <span className="text-[#0A0A0F]">{line}</span>
                )}
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="text-base md:text-lg text-[#62646B] mt-7 max-w-xl leading-relaxed"
          >
            {t(HERO.subhead)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            data-testid="hero-cta"
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <a
              href="/reserve"
              data-testid="hero-cta-reserve"
              className="group wf-shimmer inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors duration-300"
            >
              {t(HERO.ctaPrimary)}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#token"
              data-testid="hero-cta-token"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-[#E8E7E2] hover:border-[#0A0A0F] text-[#0A0A0F] font-semibold transition-colors duration-300"
            >
              {t(HERO.ctaSecondary)}
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            data-testid="hero-stats"
            className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-12 max-w-3xl"
          >
            {HERO_STATS.map((s, i) => (
              <div
                key={i}
                data-testid={`hero-stat-${i}`}
                className="p-4 rounded-2xl border border-[#E8E7E2] bg-[#F4F2EC]/70"
              >
                <p className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1F]">{t(s.value)}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#62646B] mt-1.5 leading-tight">
                  {t(s.label)}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* RIGHT — Token card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="hidden lg:flex flex-col items-center justify-center"
          data-testid="hero-token-card"
        >
          <div className="relative wf-glass rounded-3xl p-8 wf-glow border border-[#E8E7E2] max-w-sm w-full">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#62646B] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0F]" />
                {t(HERO.tokenCardLabel)}
              </span>
              <span className="text-2xl">🏔️</span>
            </div>

            <div className="my-8 flex justify-center">
              <motion.img
                src="/img/winn-coin.png"
                alt="Winfield Token coin"
                className="w-44 h-44 object-contain wf-coin-glow"
                animate={{ y: [0, -14, 0], rotate: [0, 4, 0, -4, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>

            <div className="text-center">
              <p className="font-heading text-2xl font-semibold tracking-tight">$WINN</p>
              <p className="text-xs uppercase tracking-[0.25em] text-[#62646B] mt-2">
                {t(HERO.tokenCardTag)}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
